import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAccess } from '../../hooks/useAccess';
import { findFirstAccessibleLeaf } from '../../constants/adminMenus';

/**
 * Landing route for "/" and for every post-login redirect.
 *
 * Sends each admin to the first sidebar entry they can actually open instead
 * of a hard-coded `/dashboard`: an admin whose role lacks `admin_dashboard`
 * was previously bounced to /403 immediately after logging in.
 */
const HomeRedirect: React.FC = () => {
  const { has, isLoading, error } = useAccess();

  // Same rule as ProtectedRoute: token presence decides "logged in", so an
  // anonymous visit to "/" goes to the login page without firing /me/access.
  if (!localStorage.getItem('accessToken')) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Snapshot failed (network/HTTP). Fall back to the dashboard rather than
  // stranding the user on /403 — ProtectedRoute also fails open in this case
  // and the backend still enforces every request.
  if (error) {
    return <Navigate to="/dashboard" replace />;
  }

  const leaf = findFirstAccessibleLeaf(has);
  return <Navigate to={leaf ? leaf.path : '/403?reason=no_permission'} replace />;
};

export default HomeRedirect;
